import { styled, useTheme } from '@mui/material/styles';
import { AppBar, IconButton, Stack, Toolbar } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import useOffSetTop from 'src/hooks/useOffSetTop';
import useResponsive from 'src/hooks/useResponsive';
import useCollapseDrawer from 'src/hooks/useCollapseDrawer';
import cssStyles from '../../../utils/cssStyles';
import useMerchant from 'src/hooks/useMerchant';
import { HEADER } from '../../../config';
import Logo from 'src/components/Logo';
import AccountPopover from './AccountPopover';
import TopAlertMessage from 'src/components/subscription/TopAlertMessage';
import CollapseButton from '../navbar/CollapseButton';
import { AppNavigationMenu } from 'src/layouts/navigation/app-navigation-menu';
import { AppTypography, StackRow } from 'src/components';
import { getNavConfig } from '../navbar/NavConfig';
import useSubscriptions from 'src/hooks/useSubscriptions';
import { IconShoppingCart } from 'src/libraries/icons';
import useCart from 'src/hooks/useCart';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { useProgramme } from 'src/hooks/use-programme';

//
//

type RootStyleProps = {
  isCollapse: boolean;
  isOffset: boolean;
};

const RootStyle = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'isCollapse' && prop !== 'isOffset',
})<RootStyleProps>(({ isCollapse, isOffset, theme }) => ({
  ...cssStyles(theme).bgBlur(),
  border: 'none',
  boxShadow: 'none',
  zIndex: theme.zIndex.appBar + 1,
  transition: theme.transitions.create(['width', 'height'], {
    duration: theme.transitions.duration.shorter,
  }),
  '& .MuiChip-root': {
    color: theme.palette.text.primary,
    '&:hover': {
      '& .MuiTypography-root': {
        fontFamily: 'ObjektivMk1 !important',
        fontWeight: 500,
      },
      '& .MuiSvgIcon-root': {
        transform: 'scale(1.1)'
      },
    },
    '&:active': {
      boxShadow: 'none',
    }
  }
}));

//

type Props = {
  onOpenSidebar: VoidFunction;
  isCollapse?: boolean;
};

export default function DashboardHeader({ onOpenSidebar, isCollapse = false }: Props) {
  const isOffset = useOffSetTop(HEADER.DASHBOARD_DESKTOP_HEIGHT);

  const isDesktop = useResponsive('up', 'lg');
  const mdUp = useResponsive('up', 'md');

  const { merchant } = useMerchant();
  const { features, currentPlan } = useSubscriptions();
  const { programmes } = useProgramme();
  const { collapseClick } = useCollapseDrawer();

  return (
    <RootStyle isCollapse={isCollapse} isOffset={isOffset}>
      <TopAlertMessage />

      <Toolbar sx={{ height: 96, borderBottom: theme => `1px solid ${theme.palette.divider}` }}>
        <StackRow justifyContent="space-between" flex={1}>
          <StackRow flex={1}>
            {!mdUp && (
              <CollapseButton onToggleCollapse={onOpenSidebar} collapseClick={collapseClick} />
            )}

            <StackRow alignItems="center" justifyContent="flex-start" flex={1}>
              <Logo sx={{ '& img': { height: 24 } }} />

              {mdUp && (
                <AppNavigationMenu navConfig={getNavConfig(programmes, features, currentPlan?.packageCode)} />
              )}
            </StackRow>
          </StackRow>

          <StackRow alignItems="center" gap={isDesktop ? 3 : 1 }>
            <CartButton />

            <StackRow gap={0}>
              {isDesktop && (
                <AppTypography
                  variant="body2"
                  sx={{
                    color: 'text.primary',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    maxWidth: { xs: 350, md: '100%' },
                  }}
                >
                  {merchant?.name}
                </AppTypography>
              )}
              <AccountPopover />
            </StackRow>
          </StackRow>
        </StackRow>
      </Toolbar>
    </RootStyle>
  );
}

//

const CartButton = () => {
  const { cartData } = useCart();
  const theme = useTheme();

  const fId = cartData?.footprintId;
  const erId = cartData?.emissionRecordId;

  const path = `${PATH_DASHBOARD.cart.root}${fId ? `?footprintId=${fId}` : erId ? `?emissionRecordId=${erId}` : ''}`

  //

  return (
    <IconButton
      component={RouterLink}
      size="small"
      sx={{ position: 'relative' }}
      to={path}
    >
      {!!cartData?.products?.length && (
        <Stack
          sx={{
            width: 14,
            height: 14,
            background: theme.palette.primary.main,
            color: 'white',
            borderRadius: '50%',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'absolute',
            right: 0,
            top: 0,
          }}
        >
          <AppTypography fontWeight="bold" fontSize={7}>
            {cartData?.products.length}
          </AppTypography>
        </Stack>
      )}
      <IconShoppingCart sx={{ color: theme.palette.text.primary }} />
    </IconButton>
  );
};
